<template>
  <div>
    <article class="story">
      <header>
        <h1>{{ storyContent.header }}<span>{{ storyContent.country }}</span></h1>
        <img
          :srcset="
            require(`@/assets/img/${$route.name}/main-sm.jpg`) + ' 960w, ' +
            require(`@/assets/img/${$route.name}/main-md.jpg`) + ' 1920w, ' +
            require(`@/assets/img/${$route.name}/main-lg.jpg`) + ' 3840w'"
          :src="require(`@/assets/img/${this.$route.name}/main-md.jpg`)"
          :alt="storyContent.headerImageDescription"
          width="1920"
          height="1080"
          :class="storyContent.headerImageAlign ? 'align-' + storyContent.headerImageAlign : ''" />
      </header>
      <main id="story-content">
        <section class="credits">
          <div class="container">
            <h1>{{ storyContent.header }}<span> / {{ storyContent.country }}</span></h1>
            <p><em>Story by </em>
              <span v-for="(person, index) in storyContent.storyBy" :key="index" itemprop="creator" itemscope itemtype="http://schema.org/Person">
                {{ person }}<template v-if="index < storyContent.storyBy.length - 2">, </template><template v-if="index === storyContent.storyBy.length - 2"> and </template>
              </span>
            </p>
            <p v-if="storyContent.reportingBy"><em>Additional reporting by </em>
              <span v-for="(person, index) in storyContent.reportingBy" :key="index" itemprop="creator" itemscope itemtype="http://schema.org/Person">
                {{ person }}<template v-if="index < storyContent.reportingBy.length - 2">, </template><template v-if="index === storyContent.reportingBy.length - 2"> and </template>
              </span>
            </p>
          </div>
        </section>
        <section>
          <div class="container">
            <p v-for="(paragraph, index) in storyContent.storyDescription" :key="`'desc-'${index}`"><template v-if="paragraph.lead"><em>{{ paragraph.lead }}</em></template>{{ paragraph.txt }}</p>
          </div>
        </section>
        <section class="photos">
          <template v-for="(image, index) in storyContent.images">
            <!-- Load with interaction observer -->
            <StoryImage
              :key="`'img-'${index}`"
              :alignClass="image.align ? 'align ' + image.align : 'align centre'"
              :imgSet="
                require(`@/assets/img/${$route.name}/${image.src}-sm.jpg`) + ' 960w, ' +
                require(`@/assets/img/${$route.name}/${image.src}-md.jpg`) + ' 1920w, ' +
                require(`@/assets/img/${$route.name}/${image.src}-lg.jpg`) + ' 3840w'"
              :imgSrc="require(`@/assets/img/${$route.name}/${image.src}-md.jpg`)"
              :imgHeight="image.height"
              :captionDateline="image.dateline"
              :caption="image.caption"
              :credit="image.credit"
              :alt="image.alt"
             />
          </template>
        </section>
      </main>
    </article>
    <StoryNavigation />
  </div>
</template>

<script>
import StoryNavigation from './StoryNavigation'
import StoryImage from './StoryImage'

export default {
  metaInfo() {

    const description = this.storyContent.audioDescription
    const title = this.storyContent.header + '/' + this.storyContent.country
    const link = 'https://globalreportingprogram.org/beyond-school/' + this.$route.name
    const socialImgUrl = 'https://globalreportingprogram.org' + require(`@/assets/img/${this.$route.name}/social.jpg`)

    return {
      title: title,
      link: [{
        vmid: 'canonical-url',
        rel: 'canonical',
        href: link
      }],
      meta: [
        {
        vmid: 'page-description',
        name: 'description',
        content: description
        },
        {
          vmid: 'og-title',
          property: 'og:title',
          content: title
        },
        {
          vmid: 'og-url',
          property: 'og:url',
          content: link
        },
        {
          vmid: 'og-image',
          property: 'og:image',
          content: socialImgUrl
        },
        {
          vmid: 'og-description',
          property: 'og:description',
          content: description
        },
        {
          vmid: 'twitter-title',
          name: 'twitter:title',
          content: title
        },
        {
          vmid: 'twitter-description',
          name: 'twitter:description',
          content: description
        },
        {
          vmid: 'twitter-image',
          name: 'twitter:image',
          content: socialImgUrl
        },
        {
          vmid: 'schema-name',
          itemprop: 'name',
          content: title
        },
        {
          vmid: 'schema-description',
          itemprop: 'description',
          content: description
        },
        {
          vmid: 'schema-image',
          itemprop: 'image',
          content: socialImgUrl
        }
      ]
    }
  },
  name: 'SingleStory',
  data() {
    return {
      runTime: this.storyContent.audioLength ? this.storyContent.audioLength : '15:00'
    }
  },
  components: {
    StoryNavigation,
    StoryImage
  },
  props: {
    // Check type
    storyContent: Object,
    isPlaying: Boolean,
    activeEpisode: String
  },
  methods: {
    playPause: function(episode) {
      if (this.isPlaying && this.activeEpisode === this.storyContent.audioFileName) {
        // If audio player is playing and the active episode is the same as the loaded filename
        this.$parent.$emit('pause', episode)
      }
      else {
        this.$parent.$emit('play', episode)
      }
    }
  },
  computed: {
    runningTime: function() {
      const mins = this.runTime.split(':')[0]
      const secs = this.runTime.split(':')[1]
      return 'PT' + mins + 'M' + secs + 'S'
    }
  }
}
</script>

<style lang="scss">
article.story {
  > header h1 {
    top: 1.5rem;
    left: 2rem;
  }
  section.credits h1 {
    span { font-size: 1.3rem; }
    @include md { display: none; }
  }
  section {
    &.audio, &.credits {
      color: $white;
      padding: 1.5rem 1rem 2rem;
      margin-bottom: 1rem;
      button {
        text-align: left;
        padding: 0.25rem 0.5rem;
        color: $white;
        font-weight: 700;
        font-size: 1.1rem;
        display: flex;
        @include md {
          align-items: center;
        }
        svg {
          padding-right: 0.5rem;
          transition: border-color 0.3s;
        }
        span {
          transition: border-color 0.3s;
          text-decoration: underline;
          line-height: 1.5;
          @include md {
            border-bottom: 1px solid darken($white, 10%);
            text-decoration: none;
            line-height: 2rem;
          }
          span {
            border-bottom: 0;
          }
        }
        time {
          line-height: 2rem;
          font-size: 0.75em;
          margin-left: 0.5rem;
          @include md { color: darken($white, 20%); transition: color 0.3s; }
          font-family: monospace;
            &::before {
              content: '(';
            }
            &::after {
              content: ')';
            }
        }
        &:hover, &:focus {
          span {
            border-color: $white;
          }
          time {
            color: $white;
          }
        }
      }
      p {
        font-size: 0.9em;
        padding: 0 0.5rem;
        margin-top: 0.25rem;
        margin-bottom: 0.75rem;
      }
      a.transcript {
        padding: 0.25rem 0.5rem;
        color: $white;
        border-radius: 2px;
        transition: all 0.2s;
        font-size: 0.9em;
        text-decoration: none;
        display: inline-block;
        margin: 0 0.5rem;
        @include focus-shadow;
        &:hover, &.active {
          color: $black;
          background-color: $white;
        }
      }
    }
    &.credits {
      p {
        margin-bottom: 0;
        padding: 0;

        + p {
          margin-top: 1rem;
        }
      }
    }
    &.photos {
      overflow: hidden;
      padding: 0;
        @include md { padding: 1rem; }
    }
  }
}

.kenya article.story, .pakistan article.story {
  > header h1 {
    top: auto;
    right: 2rem;
    bottom: 1.5rem;
    left: auto;
    text-align: right;
  }
}
</style>